<template>
  <div class="admin-page">
        <div class="admin-toolbar">
      <div class="admin-toolbar__actions">
            <el-button
              type="danger"
              :disabled="selectedRows.length === 0"
              @click="batchDelete"
            >批量删除 ({{ selectedRows.length }})</el-button>
            <el-button type="primary" @click="refreshList">刷新列表</el-button>
          </div>
        </div>
        
        <el-table
          ref="tableRef"
          :data="auditImages"
          v-loading="loading"
          style="width: 100%"
          @selection-change="handleSelectionChange"
        >
          <el-table-column type="selection" width="48" />
          <el-table-column prop="photoId" label="图片ID" width="100" />
          <el-table-column prop="userId" label="用户ID" width="100" />
          <el-table-column label="图片预览" width="150">
            <template #default="scope">
              <el-image
                :src="photoPathToUrl(scope.row.photoUrl)"
                :preview-src-list="[photoPathToUrl(scope.row.photoUrl)]"
                :preview-teleported="true"
                :z-index="5000"
                fit="cover"
                style="width: 120px; height: 120px"
              />
            </template>
          </el-table-column>
          <el-table-column prop="isAudited" label="审核状态" width="120">
            <template #default="scope">
              <el-tag 
                :type="getAuditStatusType(scope.row.isAudited)"
                size="small"
              >
                {{ getAuditStatusText(scope.row.isAudited) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="createdAt" label="上传时间" width="180">
            <template #default="scope">
              {{ formatDate(scope.row.createdAt) }}
            </template>
          </el-table-column>
          <el-table-column prop="auditedAt" label="审核时间" width="180">
            <template #default="scope">
              {{ scope.row.auditedAt ? formatDate(scope.row.auditedAt) : '-' }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" v-if="showAuditActions">
            <template #default="scope">
              <el-button 
                size="small" 
                type="success" 
                @click="auditImage(scope.row, 1)"
                :disabled="scope.row.isAudited !== 0"
              >通过</el-button>
              <el-button 
                size="small" 
                type="danger" 
                @click="auditImage(scope.row, 2)"
                :disabled="scope.row.isAudited !== 0"
              >拒绝</el-button>
            </template>
          </el-table-column>
        </el-table>
        
        <AdminPagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import AdminPagination from '@/components/AdminPagination.vue'
import axios from 'axios'
import { photoPathToUrl } from '@/utils/photoUpload'

export default {
  name: 'ImageManagement',
  components: {
    AdminPagination
  },
  setup() {
    const loading = ref(false)
    const auditImages = ref([])
    const currentPage = ref(1)
    const pageSize = ref(10)
    const total = ref(0)
    const selectedRows = ref([])
    const tableRef = ref(null)

    const showAuditActions = computed(() => {
      return auditImages.value.some(img => img.isAudited === 0)
    })

    const formatDate = (timestamp) => {
      if (!timestamp) return ''
      const date = new Date(typeof timestamp === 'number' ? timestamp * 1000 : timestamp)
      return date.toLocaleString()
    }

    const getAuditStatusType = (status) => {
      switch (status) {
        case 0: return 'warning'
        case 1: return 'success'
        case 2: return 'danger'
        default: return 'info'
      }
    }

    const getAuditStatusText = (status) => {
      switch (status) {
        case 0: return '待审核'
        case 1: return '已通过'
        case 2: return '已拒绝'
        default: return '未知'
      }
    }

    const authHeaders = () => ({
      Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
      'Content-Type': 'application/json'
    })

    const fetchAuditImages = async () => {
      loading.value = true
      try {
        const response = await axios.post('/api/v1/admin/user/image/audit/list', {
          page: currentPage.value,
          pageSize: pageSize.value
        }, { headers: authHeaders() })
        
        if (response.data.code === 200) {
          auditImages.value = response.data.data.photos
          total.value = response.data.data.total
          selectedRows.value = []
        } else {
          ElMessage.error(response.data.message || '获取审核图片列表失败')
        }
      } catch (error) {
        console.error('Error fetching audit images:', error)
        ElMessage.error('获取审核图片列表失败')
      } finally {
        loading.value = false
      }
    }

    const auditImage = async (image, auditResult) => {
      try {
        const actionText = auditResult === 1 ? '通过' : '拒绝'
        let remark = ''
        if (auditResult === 2) {
          const { value } = await ElMessageBox.prompt('请填写拒绝原因（将发送给用户）', '拒绝图片', {
            confirmButtonText: '拒绝',
            cancelButtonText: '取消',
            inputPlaceholder: '例如：请上传清晰正面照',
            inputType: 'textarea',
            type: 'warning'
          })
          remark = value || ''
        } else {
          await ElMessageBox.confirm(`确定要${actionText}这张图片吗？`, '确认审核', {
            type: 'warning'
          })
        }

        const response = await axios.post('/api/v1/admin/user/image/audit', {
          photoId: image.photoId,
          isAudited: auditResult,
          remark
        }, { headers: authHeaders() })
        
        if (response.data.code === 200) {
          ElMessage.success(`图片${actionText}成功`)
          fetchAuditImages()
        } else {
          ElMessage.error(response.data.message || `图片${actionText}失败`)
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error auditing image:', error)
          ElMessage.error('审核图片失败')
        }
      }
    }

    const handleSelectionChange = (rows) => {
      selectedRows.value = rows
    }

    const batchDelete = async () => {
      if (selectedRows.value.length === 0) return
      try {
        await ElMessageBox.confirm(
          `确定要删除选中的 ${selectedRows.value.length} 张图片吗？此操作不可恢复。`,
          '确认删除',
          { type: 'warning' }
        )
        const photoIds = selectedRows.value.map(row => row.photoId)
        const response = await axios.post('/api/v1/admin/user/image/audit/delete', {
          photoIds
        }, { headers: authHeaders() })

        if (response.data.code === 200) {
          ElMessage.success(response.data.message || '删除成功')
          fetchAuditImages()
        } else {
          ElMessage.error(response.data.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error deleting images:', error)
          ElMessage.error('删除图片失败')
        }
      }
    }

    const handleSizeChange = (val) => {
      pageSize.value = val
      currentPage.value = 1
      fetchAuditImages()
    }

    const handlePageChange = (val) => {
      currentPage.value = val
      fetchAuditImages()
    }

    const refreshList = () => {
      fetchAuditImages()
    }

    onMounted(() => {
      fetchAuditImages()
    })

    return {
      loading,
      auditImages,
      currentPage,
      pageSize,
      total,
      selectedRows,
      tableRef,
      showAuditActions,
      formatDate,
      getAuditStatusType,
      getAuditStatusText,
      handleSelectionChange,
      handleSizeChange,
      handlePageChange,
      auditImage,
      batchDelete,
      refreshList,
      photoPathToUrl
    }
  }
}
</script>

